import { defineComponent as _defineComponent } from 'vue'
import ScaleOut from "@/components/scale-out-component.vue";
import BreadCrumbNavigation from "@/components/bread-crumb-component.vue";
import DocumentContent from "@/components/document-content.vue";
import { addSeconds, format } from "date-fns";
import { onMounted, ref, Ref } from "vue";
import Examination from "@/interfaces/examination.interface";
import { useRoute, useRouter } from "vue-router/composables";
import useHttpRequest from "@/composables/use-http-request";
import { useShowWarningMessage } from "@/composables/use-notifications";
import TextEditorComponent from "@/components/text-editor-component.vue";
import SubmitAnswerSheetDialog from "@/components/dialogs/submit-answer-sheet-dialog.vue";
import EditorGetChangesErrorDialog from "@/components/dialogs/editor-get-changes-error-dialog.vue";
import { Editor } from "tinymce";
import { DraftStorage } from "@/helpers/onlineAnswerSheet/draftStorage";
import { useSubmitExamination } from "@/composables/examinations/use-submit-examination";
import { useManageStudentChanges } from "@/composables/examinations/use-manage-student-changes";
import { useAssessmentCountDown } from "@/composables/assessments/use-assessment-countdown";
import ExaminationAttachmentsDialog from "@/components/dialogs/examination-attachments-dialog.vue";
import { useOnlineAnswersheetAttachments } from "@/composables/online-answersheet/use-online-answersheet-attachments";


export default /*#__PURE__*/_defineComponent({
  __name: 'ongoing-examination',
  setup(__props) {

const IS_WORKERS_SUPPORTED = typeof Worker !== "undefined";

const route = useRoute();
const router = useRouter();

const isLoading: Ref<boolean> = ref(false);
const activeView: Ref<"questionPaper" | "answerSheet"> = ref("questionPaper");
const submissionType = ref<"Online" | "File Upload">();
const examination: Ref<Examination | null> = ref(null);
const examinationId = ref(route.params.examId);

let examEndsAtEpochTimeInSecs = ref(0);

let textEditorInstance: Ref<Editor | undefined> = ref();
let draftStorage: Ref<DraftStorage | undefined> = ref(undefined);

let lastSavedAt: Ref<number | undefined> = ref();

const isUploadAnswerSheetDialogVisible = ref(false);

//Manage Examination Count Down clock
const { beginAssessmentTimer } = useAssessmentCountDown({
  endsAtEpochTimeInSecs: examEndsAtEpochTimeInSecs,
});

const {
  isOnlineAnswersheetAttachmentsDialogVisible,
  attachments,
  onAttachmentRemoved,
  onAttachmentAdded,
} = useOnlineAnswersheetAttachments();

//Manage getting and saving student changes
const {
  saveStudentChanges,
  saveStudentFinalChanges,
  onEditorSaveChangesFailed,
  showEditorContentSaveErrorDialog,
  onChangesFailedRedirectTo,
} = useManageStudentChanges({
  draftStorage,
  examination,
  textEditorInstance,
});

//Manage answer sheet submission
const {
  onSubmitFileUploadAnswerSheet,
  onSubmitOnlineAnswerSheet,
  onSubmitAnswerSheetIntent,
} = useSubmitExamination({
  examination,
  saveStudentChanges,
  saveStudentFinalChanges,
  isUploadAnswerSheetDialogVisible,
  submissionType,
  draftStorage,
});

const onTextEditorReady = (editor: Editor) => {
  textEditorInstance.value = editor;
};

const getExamination = async () => {
  const request = await useHttpRequest<{
    success: boolean;
    message: string;
    examination: Examination;
  }>({
    method: "GET",
    url: `examinations/ongoing-examination/${
      examinationId.value
    }?timestamp=${new Date().getTime()}`,
    loadingProperty: isLoading,
    showSuccessMessage: false,
    errorLoadingProperty: null,
    showFullScreenLoader: false,
  });

  if (!request) {
    return router.back();
  }

  if (
    request.success &&
    request.message == "ONGOING EXAMINATION FETCHED SUCCESSFULLY"
  ) {
    examination.value = request.examination;
    examEndsAtEpochTimeInSecs.value = +addSeconds(
      new Date(),
      request.examination.ends_in_seconds
    );

    const submission = examination.value.student_examination_submissions?.[0];

    if (submission) {
      attachments.value = submission.attachments.map((attachment) => {
        return {
          id: attachment.uuid,
          title: attachment.title,
          file_path: attachment.file_path_url,
          file_size: attachment.file_size,
        };
      });
    }

    if (submission && submission.special_submission_ends_in_seconds) {
      if (submission.special_submission_ends_in_seconds) {
        examEndsAtEpochTimeInSecs.value = +addSeconds(
          new Date(),
          submission.special_submission_ends_in_seconds
        );
      }
    }

    //If we have the aws_online_answersheet_path, means there could be some info in online submissions for the user
    const DRAFT_KEY = `draft-${submission?.uuid}`;
    const DRAFT_TIMESTAMP_KEY = `${DRAFT_KEY}-updated`;

    draftStorage.value = new DraftStorage({
      draftKey: DRAFT_KEY,
      draftTimestampKey: DRAFT_TIMESTAMP_KEY,
    });

    beginAssessmentTimer();

    //If the exam is not available for taking yet, then dont accept them to even view this page at the moment, just redirect them to Examinations UI
  } else {
    return router.back();
  }
};

const handleClick = async (command: string) => {
  if (command == "AnswerSheet") {
    // if (!examination.value?.answer_sheet_path_url) {
    //   return useShowWarningMessage(
    //     "No Answer Sheet",
    //     "There's No Answer Sheet attached. Please Contact Admin"
    //   );
    // }

    // const link = document.createElement("a");
    // link.href = examination.value.answer_sheet_path_url;
    // link.download = "Answer Sheet";
    // link.click();

    const submission = examination.value?.student_examination_submissions?.[0];

    const request = await useHttpRequest<{
      success: boolean;
      message: string;
      path_url: string;
    }>({
      method: "GET",
      url: `examinations/request-answersheet-word-document?student_examination_submission_id=${
        submission?.student_examination_submission_id
      }&timestamp=${new Date().getTime()}`,
      loadingProperty: null,
      showSuccessMessage: true,
      errorLoadingProperty: null,
      showFullScreenLoader: true,
    });

    if (!request) {
      return;
    }

    if (
      request.success &&
      request.message === "Answer sheet document fetched successfully"
    ) {
      //TODO: Use the download file composable

      const link = document.createElement("a");
      link.href = request.path_url;
      link.download = "Answer Sheet";
      link.click();
    }
  } else if (command == "QuestionPaper") {
    if (!examination.value?.question_paper_path_url) {
      return useShowWarningMessage(
        "No Question Paper",
        "There's No Question Paper attached. Please Contact Admin"
      );
    }

    const link = document.createElement("a");
    link.href = examination.value.question_paper_path_url;
    link.download = "Answer Sheet";
    link.click();
  }
};

const onEditorValueChange = async (content: string): Promise<void> => {
  // Save the content to local storage
  const saved = await draftStorage.value?.saveAsync(content);

  if (saved) {
    lastSavedAt.value = Date.now();
    // textEditorInstance.value?.notificationManager.open({
    //   text: "Changes saved locally",
    //   type: "info",
    //   timeout: 3000,
    // });
  }
};

onMounted(() => {
  getExamination();
});

return { __sfc: true,IS_WORKERS_SUPPORTED, route, router, isLoading, activeView, submissionType, examination, examinationId, examEndsAtEpochTimeInSecs, textEditorInstance, draftStorage, lastSavedAt, isUploadAnswerSheetDialogVisible, beginAssessmentTimer, isOnlineAnswersheetAttachmentsDialogVisible, attachments, onAttachmentRemoved, onAttachmentAdded, saveStudentChanges, saveStudentFinalChanges, onEditorSaveChangesFailed, showEditorContentSaveErrorDialog, onChangesFailedRedirectTo, onSubmitFileUploadAnswerSheet, onSubmitOnlineAnswerSheet, onSubmitAnswerSheetIntent, onTextEditorReady, getExamination, handleClick, onEditorValueChange, ScaleOut, BreadCrumbNavigation, DocumentContent, format, TextEditorComponent, SubmitAnswerSheetDialog, EditorGetChangesErrorDialog, ExaminationAttachmentsDialog }
}

})